/**
 * Wave E (decision 0063): several people in one project. Who a run works for (`runPrincipalOf`), who last shaped an item
 * (`shapeAsker`), whose machines may run it (`mayRun`), lend requests (`lendRequestsFor`), whom a card is for
 * (`recipientOf`) and the For you / Others split (`needsYouSplit`). Pure.
 *
 * In a project with one person every rule here gives today's answer: the asker is the keeper, the keeper's runners are
 * the only ones, and every card is theirs.
 */
import type { LendCandidate, ProfileAccess, RunnerServes, Unmet } from "@minga/protocol"
import { permissionMachineOf } from "./authorize"
import { isCardFor, isStaleEscalation } from "./needs-you"
import { isOpenProposal, itemOf, projectRoleOf, runOf } from "./state"
import { accountChoiceKey } from "./subscription-accounts"
import { ancestorsOf } from "./tree"
import type { Actor, Ids, Item, ItemPatch, Principal, ProjectState, Proposal, Run } from "./types"

type UserId = Ids["UserId"]
type RunnerId = Ids["RunnerId"]
type ItemId = Ids["ItemId"]
type RunId = Ids["RunId"]
type AgentId = Ids["AgentId"]

// #region members
/**
 * Is this person a working member of the project (a member whose role is not `viewer`)? Before the first membership
 * sync (no members yet) everyone counts, as `ownerLeft` does.
 */
export const isWorkingMember = (s: ProjectState, userId: UserId): boolean =>
  s.members.length === 0 || s.members.some((m) => m.userId === userId && m.role !== "viewer")

/**
 * Decision 0154 (amends 0070): whose plain note on an agent's item is an instruction, not reference material: the
 * agent's keeper, or any working member of the project (0126: members may do almost everything in their projects). A
 * viewer's note, or one from someone outside the project, stays a stranger's.
 */
export const noteTrustOf =
  (s: ProjectState, keeper: UserId | null | undefined) =>
  (userId: UserId): boolean =>
    userId === keeper || isWorkingMember(s, userId)

/** The workspace owner (a member with role `owner`), else the project's owner. */
export const workspaceOwnerOf = (s: ProjectState): UserId | null =>
  s.members.find((m) => m.role === "owner")?.userId ?? s.project?.ownerId ?? null

/** Does the project have a second person (wave E's shared-project rules apply)? */
export const isSharedProject = (s: ProjectState): boolean => s.members.length > 1

/**
 * Does this run work for a person who left (spec §5.4)? Its asker is a person who is no longer a working member, or its
 * principal (`ownerId`) is. `system:membership` cancels exactly these (reason `member_left`).
 */
export const runOfLeaver = (s: ProjectState, run: Run): boolean =>
  (run.askedBy?._tag === "user" && !isWorkingMember(s, run.askedBy.userId)) ||
  !isWorkingMember(s, run.ownerId)

const samePrincipal = (a: Principal | undefined, b: Principal | undefined): boolean =>
  a === undefined || b === undefined
    ? a === b
    : a._tag === "user"
      ? b._tag === "user" && a.userId === b.userId
      : b._tag === "agent" && a.agentId === b.agentId
// #endregion members

// #region principal
/** What `runPrincipalOf` needs of a worker or validator run about to be requested. */
export interface PrincipalInput {
  readonly kind: "worker" | "validator"
  /** Wave F's `Run.askedBy` / `Item.askedBy` (absent = the agent's own work). */
  readonly askedBy: Principal | undefined
  /** The owning agent's keeper (`Agent.ownerId`). */
  readonly keeper: UserId
}

/**
 * The principal (`Run.ownerId`) of a worker or validator run (spec §3.2): `askedBy.user` when that person is a member
 * (role ≠ viewer); the keeper when `askedBy` is an agent or absent; `null` when `askedBy` is a person who left (the
 * attempt doesn't start; the item waits for a person to edit or retry it).
 *
 * Named `runPrincipalOf` (the spec's `principalOf`): `state.ts` already exports `principalOf(actor)`.
 */
export const runPrincipalOf = (s: ProjectState, i: PrincipalInput): UserId | null => {
  const a = i.askedBy
  if (a?._tag !== "user") return i.keeper
  return isWorkingMember(s, a.userId) ? a.userId : null
}

/**
 * Wave F (decision 0062): who asked for an item's worker or validator run: the item's asker, else the person who
 * created it, else the supervising agent.
 */
export const itemAskedBy = (t: Item, ownerAgentId: AgentId): Principal =>
  t.askedBy ??
  (t.createdBy._tag === "user"
    ? { _tag: "user", userId: t.createdBy.userId }
    : { _tag: "agent", agentId: ownerAgentId })

/**
 * The person an item waits for because they left (spec §3.2: "Kate left: edit or Retry to run it as you"), or null.
 * The ready frontier skips such an item; nothing is written.
 */
export const askerWhoLeft = (s: ProjectState, t: Item): UserId | null => {
  if (t.owner?._tag !== "agent") return null
  const asked = itemAskedBy(t, t.owner.agentId)
  return asked._tag === "user" && !isWorkingMember(s, asked.userId) ? asked.userId : null
}

/**
 * Shaping (spec §3.2): the item's new `askedBy` after `actor` changed what its next attempt reads or does, or
 * `undefined` when it stays. A person → `{ user }`; an agent in a turn asked by a person → that person (always); an
 * agent on its own initiative → `{ agent }`. Workers, runners and the system shape nothing.
 */
export const shapeAsker = (
  item: Item,
  actor: Actor,
  turnAskedBy: Principal | undefined,
): Principal | undefined => {
  const next: Principal | undefined =
    actor._tag === "user"
      ? { _tag: "user", userId: actor.userId }
      : actor._tag === "agent"
        ? turnAskedBy?._tag === "user"
          ? turnAskedBy
          : { _tag: "agent", agentId: actor.agentId }
        : undefined
  return next === undefined || samePrincipal(next, item.askedBy) ? undefined : next
}

/**
 * The patch keys a shaping command adds to the item (spec §3.2, §3.3): the new `askedBy` (when it changes) and
 * `lentRunnerId: null` when a lend stands. Empty when nothing changes. Only people and agents shape.
 */
export const shapingPatch = (
  s: ProjectState,
  item: Item,
  actor: Actor,
): Pick<ItemPatch, "askedBy" | "lentRunnerId"> => {
  if (actor._tag !== "user" && actor._tag !== "agent") return {}
  // a viewer (or someone who left) never becomes the asker: their work would have nobody to run as
  if (actor._tag === "user" && !isWorkingMember(s, actor.userId)) return {}
  const turnAskedBy = actor._tag === "agent" ? runOf(s, actor.runId)?.askedBy : undefined
  const askedBy = shapeAsker(item, actor, turnAskedBy)
  return {
    ...(askedBy !== undefined ? { askedBy } : {}),
    ...(item.lentRunnerId ? { lentRunnerId: null } : {}),
  }
}

/** The `before` of a shaping patch (absent keys stay absent: `before` never holds an undefined key). */
export const shapingBefore = (
  item: Item,
  patch: Pick<ItemPatch, "askedBy" | "lentRunnerId">,
): Pick<ItemPatch, "askedBy" | "lentRunnerId"> => ({
  ...(patch.askedBy !== undefined && item.askedBy !== undefined ? { askedBy: item.askedBy } : {}),
  ...(patch.lentRunnerId !== undefined ? { lentRunnerId: item.lentRunnerId ?? null } : {}),
})

/** The keys only decide writes (`Invalid("set by Minga")` in a client's patch). */
export const MINGA_ONLY_PATCH_KEYS = ["askedBy", "lentRunnerId"] as const
// #endregion principal

// #region placement
/** The runner facts `mayRun` reads (`RunnerRecord`/`RunnerForMatch` and the hub's runner rows carry them). */
export interface MayRunRunner {
  readonly id: RunnerId
  readonly ownerId: UserId
  /** Absent = `owner` (rows from before E). */
  readonly serves?: RunnerServes | undefined
  /** Decision 0128: explicit owner and scope per profile (absent = every profile is the runner owner's login). */
  readonly profileAccess?: Readonly<Record<string, ProfileAccess>> | undefined
  /** Decision 0166 §3.6: its profiles, to find the subscription a profile runs on (Platform execution profiles). */
  readonly manifest?:
    | {
        readonly profiles: ReadonlyArray<{
          readonly id: string
          readonly subscriptionId?: string | undefined
          /** Decision 0167 §6: the profile's account, so a choice of that account covers it. */
          readonly quotaAccountKey?: string | undefined
        }>
      }
    | undefined
}

/** The run facts `mayRun` reads: its kind, its principal and its item's lend (a validator's subject item). */
export interface MayRunRun {
  /** A `turn` thinks only on its keeper's machines (absent = not a turn: PR watch, work runs). */
  readonly kind?: string | undefined
  readonly ownerId: UserId
  readonly lentRunnerId?: RunnerId | null | undefined
  /**
   * Decision 0128: the person who asked for a turn (absent or null = its keeper). A turn's `ownerId` is the keeper; a
   * turn asked by someone else runs on the asker's own or a team subscription.
   */
  readonly turnAsker?: UserId | null | undefined
  /**
   * Decision 0166 §3.2: the run's project, read by `mayUseProfile` for a subscription shared in some projects only
   * (absent or null = unknown: such a subscription never runs it for someone else).
   */
  readonly projectId?: string | null | undefined
  /**
   * Decision 0166 §3.6: the subscriptions the asker chose for their work in the run's project (`subscriptionKey`s).
   * Absent or null = nothing chosen: everything they may use.
   */
  readonly chosen?: ReadonlyArray<string> | null | undefined
}

/**
 * Decision 0166 §3.6: a subscription's key in a person's choice: its runner and the subscription a profile runs on (a
 * Platform Runner execution profile names its subscription; a machine's login is its profile).
 */
export const subscriptionKey = (runnerId: string, subscriptionId: string): string =>
  `${runnerId}:${subscriptionId}`

/** Decision 0166 §3.6: the key of the subscription this profile of this runner runs on. */
export const profileSubscriptionKey = (runner: MayRunRunner, profileId: string): string =>
  subscriptionKey(
    runner.id,
    runner.manifest?.profiles.find((p) => p.id === profileId)?.subscriptionId ?? profileId,
  )

/**
 * Decision 0166 §3.6: is this profile among what the run's asker chose for the run's project (true when they chose
 * nothing)? Decision 0167 §6: a chosen account (`acct:<ref>` of the profile's owner and account key) covers each of its
 * connections.
 */
export const chosenFor = (
  runner: MayRunRunner,
  profileId: string,
  run: Pick<MayRunRun, "chosen">,
): boolean => {
  if (run.chosen === undefined || run.chosen === null) return true
  if (run.chosen.includes(profileSubscriptionKey(runner, profileId))) return true
  const accountKey = runner.manifest?.profiles.find((p) => p.id === profileId)?.quotaAccountKey
  return (
    accountKey !== undefined &&
    accountKey !== "" &&
    run.chosen.includes(accountChoiceKey(profileAccessOf(runner, profileId).ownerId, accountKey))
  )
}

/** Decision 0166 §3.6: the key of a person's choice in one project (`HubView.choices`). */
export const choiceKey = (userId: string, projectId: string): string => `${userId}:${projectId}`

/**
 * Decision 0128 (0126 §5): whose subscriptions a run may use: a turn's asker (absent = the keeper), else the run's
 * principal (a worker's or validator's `ownerId` is already its asker, wave E).
 */
export const runAskerOf = (run: MayRunRun): UserId =>
  run.kind === "turn" ? (run.turnAsker ?? run.ownerId) : run.ownerId

/** A turn its keeper asked for (or nobody did): it keeps thinking on the keeper's machines (wave E). */
const keeperTurn = (run: MayRunRun): boolean => run.kind === "turn" && runAskerOf(run) === run.ownerId

const lentHere = (runner: MayRunRunner, run: MayRunRun): boolean =>
  run.kind !== "turn" &&
  run.lentRunnerId !== undefined &&
  run.lentRunnerId !== null &&
  run.lentRunnerId === runner.id

/**
 * May this runner take this run at all (spec §3.3; decision 0128)? The machine-level check, before any profile.
 * A turn: on its keeper's machines, or on a machine shared with the team (the Platform Runner is one) when a profile
 * there may run it (`mayUseProfile`). Any other run: `runner.ownerId === run.ownerId ∨ runner.serves === "team" ∨
 * run.lentRunnerId === runner.id`. One predicate for placement, the hub's lease re-check, PR watch and the invariant.
 */
export const mayRun = (runner: MayRunRunner, run: MayRunRun): boolean =>
  runner.ownerId === run.ownerId || runner.serves === "team" || lentHere(runner, run)

/**
 * Decision 0128 (0126 §5): a profile's owner and scope. Explicit (`profileAccess`: every Platform Runner subscription,
 * a local profile its owner set) or, without an entry, the runner owner's login: Team on a machine shared with the
 * team, else Personal. `explicit` says which.
 */
export const profileAccessOf = (
  runner: MayRunRunner,
  profileId: string,
): ProfileAccess & { readonly explicit: boolean } => {
  const set = runner.profileAccess?.[profileId]
  return set
    ? { ...set, explicit: true }
    : { ownerId: runner.ownerId, scope: runner.serves === "team" ? "team" : "personal", explicit: false }
}

/**
 * Decision 0166 §3.2: does this access share the profile with other people's work in `projectId`? `team` = every project
 * of the workspace (rows from before 0166 read so, no rewrite); `projects` = the listed ones; `personal` = none. An
 * unknown project (absent or null) is shared only by `team`.
 */
export const sharedIn = (
  access: Pick<ProfileAccess, "scope" | "projectIds">,
  projectId: string | null | undefined,
): boolean =>
  access.scope === "team" ||
  (access.scope === "projects" &&
    projectId !== undefined &&
    projectId !== null &&
    (access.projectIds ?? []).includes(projectId as never))

/**
 * Decision 0166 §3.2: the access after its owner shares it in `projectId` (a share card's "Share in <project>"): a
 * `team` one stays; otherwise the project is added to the listed ones. Without a project it becomes `team` (0128).
 */
export const shareInProject = (
  access: ProfileAccess,
  projectId: string | null | undefined,
): ProfileAccess => {
  if (access.scope === "team") return { ownerId: access.ownerId, scope: "team" }
  if (projectId === undefined || projectId === null) return { ownerId: access.ownerId, scope: "team" }
  const listed = access.scope === "projects" ? (access.projectIds ?? []) : []
  return {
    ownerId: access.ownerId,
    scope: "projects",
    projectIds: listed.includes(projectId as never) ? listed : [...listed, projectId as never],
  }
}

/**
 * Decision 0128 (0126 §5): may this run use this profile (a subscription) on this runner? The machine must take it
 * (`mayRun`); then the asker's own subscription, or one shared with the team. A lend (wave E) lends a personal machine
 * with its owner's logins. A turn its keeper asked keeps wave E's rule for a profile without an explicit scope (its
 * keeper's machines only), so a machine shared with the team does not start hosting other people's agents by itself.
 * Decision 0166 §3.2: a subscription shared in some projects runs others' work only in those (`sharedIn`). §3.6: and
 * only one the asker chose for the run's project, when they chose (`chosenFor`).
 */
export const mayUseProfile = (runner: MayRunRunner, profileId: string, run: MayRunRun): boolean => {
  if (!mayRun(runner, run)) return false
  // decision 0166 §3.6: never a subscription the asker left out of their choice for this project
  if (!chosenFor(runner, profileId, run)) return false
  const access = profileAccessOf(runner, profileId)
  if (lentHere(runner, run) && runner.serves !== "team" && access.ownerId === runner.ownerId) return true
  if (access.ownerId === runAskerOf(run)) return true
  if (!sharedIn(access, run.projectId)) return false
  return access.explicit || !keeperTurn(run)
}

/** One row of a runner owner's For you: "Run DIC-155 on jannik-mbp?" (spec §3.3, §15 decision 2(a)). */
export interface LendRequest {
  readonly itemId: ItemId
  /** The waiting run that asked (a worker or validator of the item). */
  readonly runId: RunId
  readonly runnerId: RunnerId
  /** The runner's owner: the viewer the row is for. */
  readonly ownerId: UserId
  /** The item's asker ("Asked by Becky"). */
  readonly askedBy: Principal | undefined
  /** What only that runner meets ("needs Xcode"). */
  readonly needs: ReadonlyArray<Unmet>
  /**
   * Decision 0128: a share row, not a lend: the viewer's personal subscription (this profile) would run it if they made
   * it Team ("Share your Claude Max with the team?"). Absent = lend the machine.
   */
  readonly profileId?: Ids["ProfileId"]
}

/** The waiting state's lend candidates of a run (empty when it isn't waiting or has none). */
export const lendableOf = (run: Run): ReadonlyArray<LendCandidate> =>
  run.state._tag === "waiting" ? (run.state.lendable ?? []) : []

/**
 * The lend rows for `viewer`: one per open item with a waiting worker or validator run whose `Run.waiting.lendable`
 * names a runner of the viewer, unless the item is already lent to that runner. Pure over project state (the hub found
 * the candidates, `LendCandidate`). Ordered by the run's wait, oldest first.
 */
export const lendRequestsFor = (s: ProjectState, viewer: UserId): ReadonlyArray<LendRequest> => {
  const out: Array<LendRequest & { readonly since: number }> = []
  const seen = new Set<ItemId>()
  for (const run of s.runs) {
    if ((run.kind !== "worker" && run.kind !== "validator") || run.itemId === null) continue
    if (run.state._tag !== "waiting" || seen.has(run.itemId)) continue
    const item = itemOf(s, run.itemId)
    if (!item || item.status === "done" || item.status === "cancelled") continue
    const c = lendableOf(run).find((x) => x.ownerId === viewer && x.runnerId !== item.lentRunnerId)
    if (!c) continue
    seen.add(run.itemId)
    out.push({
      itemId: item.id,
      runId: run.id,
      runnerId: c.runnerId,
      ownerId: c.ownerId,
      askedBy: item.askedBy ?? run.askedBy,
      needs: c.needs,
      ...(c.profileId ? { profileId: c.profileId } : {}),
      since: run.state.since,
    })
  }
  return out.sort((a, b) => a.since - b.since).map(({ since: _since, ...r }) => r)
}

/** The owner of a runner a waiting run of `itemId` names as lendable, or undefined. */
export const lendOwnerOf = (s: ProjectState, itemId: ItemId, runnerId: RunnerId): UserId | undefined => {
  for (const run of s.runs)
    if (run.itemId === itemId && (run.kind === "worker" || run.kind === "validator")) {
      // decision 0128: a share candidate (a subscription) never authorizes lending its runner
      const c = lendableOf(run).find((x) => x.runnerId === runnerId && !x.profileId)
      if (c) return c.ownerId
    }
  return undefined
}

/** The live (not ended) runner runs of an item, for `LendChanged` (a leased run keeps its lease). */
export const liveRunnerRunsOf = (s: ProjectState, itemId: ItemId): ReadonlyArray<RunId> =>
  s.runs
    .filter(
      (r) =>
        r.itemId === itemId &&
        (r.kind === "worker" || r.kind === "validator") &&
        r.state._tag !== "ended" &&
        r.executor?._tag !== "platform",
    )
    .map((r) => r.id)
// #endregion placement

// #region routing
/**
 * Decision 0126 §6 (0127): the person responsible for an item's topic: the person who owns it or the nearest item above
 * it (a person-owned item), when still a working member. Never an agent's keeper (agents are the team's).
 */
export const responsiblePersonOf = (s: ProjectState, item: Item): UserId | null => {
  for (const t of [item, ...ancestorsOf(s, item)])
    if (t.owner?._tag === "user" && isWorkingMember(s, t.owner.userId)) return t.owner.userId
  return null
}

/**
 * The project's admins, in order (decision 0126 §1, 0129): the project's owner (while still its admin), the people made
 * its admins, then the workspace owners, then the workspace admins. Working members of the project only.
 */
export const projectAdminsOf = (s: ProjectState): ReadonlyArray<UserId> => {
  const ids = [
    s.project?.ownerId,
    ...s.members.filter((m) => m.projectRole === "admin").map((m) => m.userId),
    ...s.members.filter((m) => m.role === "owner").map((m) => m.userId),
    ...s.members.filter((m) => m.role === "admin").map((m) => m.userId),
  ].filter(
    (u): u is UserId =>
      u !== undefined &&
      u !== null &&
      isWorkingMember(s, u) &&
      (s.members.length === 0 || projectRoleOf(s, u) === "admin"),
  )
  return [...new Set(ids)]
}

/** The person an actor works for now: a person themselves; an agent or worker the person who asked for its run. */
export const askerOfActor = (s: ProjectState, actor: Actor): UserId | undefined => {
  if (actor._tag === "user") return actor.userId
  if (actor._tag === "agent" || actor._tag === "worker") return askerUser(runOf(s, actor.runId))
  return undefined
}

/** What a new card is about, for routing: an explicit recipient and the items it concerns. */
export interface CardRoute {
  /** The `to` the card names (already checked to be a working member). */
  readonly to?: UserId | null | undefined
  /** The items the card is about (its subject, what it changes or blocks). */
  readonly items?: ReadonlyArray<ItemId> | undefined
}

/**
 * Decision 0126 §6 (0127): whom a new card from `actor` is for. In order: the explicit `to`; the person responsible for
 * the topic (an agent sets `to` from the people pages' "Decides"; structurally, the person who owns an item the card is
 * about or one above it); the person who asked (the run's asker, a working member); the project's admins; the
 * workspace owner. The agent's keeper is no longer a default. Null only before the project has anyone.
 */
export const routeCard = (s: ProjectState, actor: Actor, route: CardRoute = {}): UserId | null => {
  const responsible = (route.items ?? []).map((id) => {
    const t = itemOf(s, id)
    return t ? responsiblePersonOf(s, t) : null
  })
  const candidates: ReadonlyArray<UserId | null | undefined> = [
    route.to,
    ...responsible,
    askerOfActor(s, actor),
    ...projectAdminsOf(s),
    workspaceOwnerOf(s),
  ]
  for (const u of candidates) if (u && isWorkingMember(s, u)) return u
  return s.project?.ownerId ?? null
}

/**
 * The one person an item's own cards and notices are for (an escalation, a review, a stuck row, "completed"): the
 * person responsible for it, else its asker, else the project's admins (decision 0126 §6, 0127).
 */
export const itemRecipientOf = (s: ProjectState, item: Item): UserId | null => {
  const asked = item.askedBy?._tag === "user" ? item.askedBy.userId : undefined
  const candidates = [responsiblePersonOf(s, item), asked, ...projectAdminsOf(s), workspaceOwnerOf(s)]
  for (const u of candidates) if (u && isWorkingMember(s, u)) return u
  return s.project?.ownerId ?? null
}
// #endregion routing

// #region cards
/** Runner facts for `recipientOf`: a permission prompt on a personal machine is for that machine's owner. */
export type RunnerFactsOf = (
  runnerId: RunnerId,
) => { readonly ownerId: UserId; readonly serves: RunnerServes } | undefined

const askerUser = (run: Run | undefined): UserId | undefined =>
  run?.askedBy?._tag === "user" ? run.askedBy.userId : undefined

/** The run that opened a card (an agent's turn or a worker), if it is in state. */
const openingRun = (s: ProjectState, p: Proposal): Run | undefined => {
  const by = p.proposedBy
  return by._tag === "agent" || by._tag === "worker" ? runOf(s, by.runId) : undefined
}

/**
 * Who a permission prompt is for (spec §4.4): on a personal machine its owner; on a team machine (or with no machine)
 * the run's asker, else its principal. The machine is the one the Project DO stamped on the card when it opened
 * (`body.machine`), so every reader agrees; else the runner facts passed in; else the run's principal stands in for
 * the machine's owner.
 */
const permissionRecipients = (
  s: ProjectState,
  p: Proposal,
  runners?: RunnerFactsOf,
): ReadonlyArray<UserId> => {
  if (p.body._tag !== "permission") return []
  const run = runOf(s, p.body.runId)
  if (!run) return []
  const team = () => [askerUser(run), run.ownerId].filter((u) => u !== undefined)
  const facts = permissionMachineOf(s, p, runners)
  if (facts?.serves === "owner") return [facts.ownerId]
  return team()
}

/**
 * The one person a card is for (spec §4.4; decision 0126 §6, 0127). Permission prompt on a personal machine → that
 * machine's owner; on a team machine → the run's asker, else its principal; human approver (routed when the card
 * opened: `to`, the topic's person, the asker, the admins) → that person; a card on its way up the chain → the person
 * it ends with; a card a turn or run opened → that run's asker; lead approver → `escalateTo`; then `p.ownerId`, the
 * project's admins, the workspace owner. The agent's keeper is not a fallback. A non-member counts as absent.
 */
export const recipientOf = (s: ProjectState, p: Proposal, runners?: RunnerFactsOf): UserId => {
  const candidates: ReadonlyArray<UserId | undefined | null> = [
    ...permissionRecipients(s, p, runners),
    p.approver._tag === "human" ? p.approver.userId : undefined,
    // decision 0102: a card on its way up the chain is for the person it ends with
    p.approver._tag === "agent" ? p.approver.escalateTo : undefined,
    askerUser(openingRun(s, p)),
    p.approver._tag === "lead" ? p.approver.escalateTo : undefined,
    p.ownerId,
    ...projectAdminsOf(s),
    workspaceOwnerOf(s),
  ]
  for (const u of candidates) if (u && isWorkingMember(s, u)) return u
  return p.ownerId
}

/**
 * The one person a stuck item is for (spec §4.4, §7.3; decision 0127): `itemRecipientOf` (the person responsible for
 * it, else its asker, else the project's admins). Anyone who may retry it still may; the others see it under Others,
 * "for Becky".
 */
export const stuckRecipientOf = (s: ProjectState, item: Item): UserId | null => itemRecipientOf(s, item)

/** `{ for }` when the decider is someone other than the card's recipient ("decided by Kate for Becky"), else `{}`. */
export const decidedFor = (
  s: ProjectState,
  p: Proposal,
  decider: Actor,
  runners?: RunnerFactsOf,
): { readonly for?: UserId } => {
  if (decider._tag !== "user") return {}
  const to = recipientOf(s, p, runners)
  return to !== decider.userId ? { for: to } : {}
}

/** Needs you, split for one viewer (spec §4.4, §7.3). */
export interface NeedsYouSplit {
  /** Open cards whose recipient is the viewer (the header count counts these, with the tab's tiers). */
  readonly forYou: ReadonlyArray<Proposal>
  /**
   * Every other open card, with its recipient; decision 0102: `agentId` when an agent holds it on its way up the chain
   * (the person may still decide it).
   */
  readonly others: ReadonlyArray<{
    readonly proposal: Proposal
    readonly recipient: UserId
    readonly agentId?: AgentId
  }>
  /** Last 7 days, decided by someone else for the viewer (`resolution.for === viewer`), at most 20. */
  readonly decidedForYou: ReadonlyArray<Proposal>
}

const DAY_MS = 86_400_000
/** "Decided for you" looks back this far. */
export const DECIDED_FOR_YOU_DAYS = 7
/** At most this many "Decided for you" rows. */
export const DECIDED_FOR_YOU_MAX = 20

/**
 * For you / Others / Decided for you. For you = `isCardFor` (open, not a stale escalation, the viewer may decide it,
 * and for a person: its recipient is the viewer). Others = every other open card the viewer may see, with its
 * recipient. Decided for you = cards someone else decided for the viewer in the last 7 days, newest first, at most 20.
 */
export const needsYouSplit = (
  s: ProjectState,
  viewer: Actor,
  now: number,
  runners?: RunnerFactsOf,
): NeedsYouSplit => {
  const open = s.proposals.filter((p) => p.state === "open" && isOpenProposal(p) && !isStaleEscalation(s, p))
  const forYou = open.filter((p) => isCardFor(s, viewer, p, runners))
  const others = open
    .filter((p) => !forYou.includes(p))
    .map((proposal) => ({
      proposal,
      recipient: recipientOf(s, proposal, runners),
      ...(proposal.approver._tag === "agent" ? { agentId: proposal.approver.agentId } : {}),
    }))
  const decidedForYou =
    viewer._tag === "user"
      ? s.proposals
          .filter(
            (p) =>
              p.resolution?.for === viewer.userId && p.resolution.at >= now - DECIDED_FOR_YOU_DAYS * DAY_MS,
          )
          .sort((a, b) => (b.resolution?.at ?? 0) - (a.resolution?.at ?? 0))
          .slice(0, DECIDED_FOR_YOU_MAX)
      : []
  return { forYou, others, decidedForYou }
}
// #endregion cards
